import React, {useState, useEffect} from 'react';
import {ArrowLeft, ArrowRight, Check, RefreshCw, Heart} from 'lucide-react';

const MultiStepForm = () => {
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(null);
    const [animating, setAnimating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        quelle_est_la_nature_de_votre_demande__: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        zip: '' // Changed from postalCode to zip
    });
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({
        general: '',
        quelle_est_la_nature_de_votre_demande__: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        zip: '' // Changed from postalCode to zip
    });

    // Map service IDs to human-readable names for HubSpot
    const serviceMap = {
        'Naturalisation': 'Naturalisation',
        'Titre de séjour': 'Titre de séjour',
        'Dossier bloqué en préfecture': 'Dossier bloqué en préfecture',
        'Accélération des procédures': 'Accélération des procédures',
        'Regroupement familial': 'Regroupement familial',
        'OQTF': 'OQTF',
    };

    const services = [
        {id: 'Naturalisation', name: 'Naturalisation'},
        {id: 'Titre de séjour', name: 'Titre de séjour'},
        {id: 'Dossier bloqué en préfecture', name: 'Dossier bloqué en préfecture'},
        {id: 'Accélération des procédures', name: 'Accélération des procédures'},
        {id: 'Regroupement familial', name: 'Regroupement familial'},
        {id: 'OQTF', name: 'OQTF'},
    ];

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const nextStep = () => {
        if (animating) return;

        // Clear previous errors
        setErrors({
            general: '',
            quelle_est_la_nature_de_votre_demande__: '',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            zip: ''
        });

        // Validate current step before proceeding
        if (step === 1 && !formData.quelle_est_la_nature_de_votre_demande__) {
            setErrors(prev => ({
                ...prev,
                quelle_est_la_nature_de_votre_demande__: "Veuillez sélectionner un type de service"
            }));
            return;
        }

        if (step === 2) {
            let hasError = false;
            const newErrors = {...errors};

            if (!formData.firstName) {
                newErrors.firstName = "Le prénom est requis";
                hasError = true;
            }

            if (!formData.lastName) {
                newErrors.lastName = "Le nom est requis";
                hasError = true;
            }

            if (hasError) {
                setErrors(newErrors);
                return;
            }
        }

        setDirection('right');
        setAnimating(true);

        // First animate current component out
        setTimeout(() => {
            setStep(prev => Math.min(prev + 1, 3));
            // After step change, allow some time for the new component to render
            setTimeout(() => {
                setAnimating(false);
            }, 50);
        }, 300);
    };

    const prevStep = () => {
        if (animating) return;

        setDirection('left');
        setAnimating(true);

        // First animate current component out
        setTimeout(() => {
            setStep(prev => Math.max(prev - 1, 1));
            // After step change, allow some time for the new component to render
            setTimeout(() => {
                setAnimating(false);
            }, 50);
        }, 300);
    };

    // Submit data to HubSpot
    const submitToHubSpot = async () => {
        setLoading(true);
        setErrors(prev => ({...prev, general: ''}));

        try {
            // Prepare data for HubSpot
            const hubspotData = {
                fields: [
                    {
                        name: "firstname",
                        value: formData.firstName
                    },
                    {
                        name: "lastname",
                        value: formData.lastName
                    },
                    {
                        name: "email",
                        value: formData.email
                    },
                    {
                        name: "phone",
                        value: formData.phone
                    },
                    {
                        name: "zip",
                        value: formData.zip
                    },
                    {
                        name: "quelle_est_la_nature_de_votre_demande__",
                        value: formData.quelle_est_la_nature_de_votre_demande__
                    }
                ],
                context: {
                    pageUri: window.location.href,
                    pageName: document.title
                }
            };

            // Your HubSpot portal ID and form ID
            const portalId = "146139336";
            const formId = "85897ef2-8fe4-4b9e-9090-78ed219d7498";

            const response = await fetch(
                `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(hubspotData)
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erreur lors de la soumission du formulaire');
            }

            // Success - show thank you page
            setSubmitted(true);
        } catch (err) {
            console.error('Error submitting to HubSpot:', err);
            setErrors(prev => ({
                ...prev,
                general: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.'
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Clear previous errors
        setErrors({
            general: '',
            quelle_est_la_nature_de_votre_demande__: '',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            zip: ''
        });

        // Validate required fields before submission
        let hasError = false;
        const newErrors = {...errors};

        if (!formData.phone) {
            newErrors.phone = "Le numéro de téléphone est requis";
            hasError = true;
        }

        if (!formData.email) {
            newErrors.email = "L'adresse email est requise";
            hasError = true;
        }

        if (!formData.zip) {
            newErrors.zip = "Le code postal est requis";
            hasError = true;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            newErrors.email = "Veuillez entrer une adresse email valide";
            hasError = true;
        }

        // Basic phone validation (adjust for French format if needed)
        const phoneRegex = /^[0-9+\s]{8,15}$/;
        if (formData.phone && !phoneRegex.test(formData.phone)) {
            newErrors.phone = "Veuillez entrer un numéro de téléphone valide";
            hasError = true;
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        // Submit to HubSpot
        submitToHubSpot();
    };

    const resetForm = () => {
        setFormData({
            quelle_est_la_nature_de_votre_demande__: '',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            zip: ''
        });
        setDirection(null);
        setStep(1);
        setSubmitted(false);
        setErrors({
            general: '',
            quelle_est_la_nature_de_votre_demande__: '',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            zip: ''
        });
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Sélectionnez un service *</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-3">
                            {services.map((service) => (
                                <label
                                    key={service.id}
                                    className={`p-4 border rounded-lg cursor-pointer transition-colors h-14
                                    ${formData.quelle_est_la_nature_de_votre_demande__ === service.id ? 'border-blue-900 bg-blue-900 text-white' : 'border-gray-200 hover:border-blue-900'}`}
                                >
                                    <input
                                        type="radio"
                                        name="quelle_est_la_nature_de_votre_demande__"
                                        value={service.id}
                                        checked={formData.quelle_est_la_nature_de_votre_demande__ === service.id}
                                        onChange={handleInputChange}
                                        className="hidden"
                                    />
                                    <div className="flex items-center justify-between w-full h-full">
                                        <span className="truncate flex-grow">{service.name}</span>
                                        <div className="w-6 flex-shrink-0">
                                            {formData.quelle_est_la_nature_de_votre_demande__ === service.id && (
                                                <Check className="text-white" size={20}/>
                                            )}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        {errors.quelle_est_la_nature_de_votre_demande__ && (
                            <div className="p-3 bg-red-100 text-red-700 rounded-md mt-2">
                                {errors.quelle_est_la_nature_de_votre_demande__}
                            </div>
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Informations personnelles</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-md font-medium text-gray-600">Prénom *</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className={`mt-1 block w-full h-8 py-4 px-2 rounded-md border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} shadow-sm focus:border-blue-900 focus:ring focus:ring-blue-900 focus:ring-opacity-50`}
                                    required
                                />
                                {errors.firstName && (
                                    <div className="p-2 bg-red-100 text-red-700 rounded-md mt-1 text-sm">
                                        {errors.firstName}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-md font-medium text-gray-600">Nom *</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className={`mt-1 block w-full h-8 py-4 px-2 rounded-md border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} shadow-sm focus:border-blue-900 focus:ring focus:ring-blue-900 focus:ring-opacity-50`}
                                    required
                                />
                                {errors.lastName && (
                                    <div className="p-2 bg-red-100 text-red-700 rounded-md mt-1 text-sm">
                                        {errors.lastName}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold">Informations de contact</h2>
                        <div className="grid gap-4">
                            <div>
                                <label className="block text-md font-medium text-gray-600">Téléphone *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className={`mt-1 block w-full h-8 py-4 px-2 rounded-md border ${errors.phone ? 'border-red-500' : 'border-gray-300'} shadow-sm focus:border-blue-900 focus:ring focus:ring-blue-900 focus:ring-opacity-50`}
                                    required
                                    placeholder="Par ex: 0612345678"
                                />
                                {errors.phone && (
                                    <div className="p-2 bg-red-100 text-red-700 rounded-md mt-1 text-sm">
                                        {errors.phone}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-md font-medium text-gray-600">Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`mt-1 block w-full h-8 py-4 px-2 rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} shadow-sm focus:border-blue-900 focus:ring focus:ring-blue-900 focus:ring-opacity-50`}
                                    required
                                    placeholder="votre.email@exemple.com"
                                />
                                {errors.email && (
                                    <div className="p-2 bg-red-100 text-red-700 rounded-md mt-1 text-sm">
                                        {errors.email}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-md font-medium text-gray-600">Code postal *</label>
                                <input
                                    type="text"
                                    name="zip"
                                    value={formData.zip}
                                    onChange={handleInputChange}
                                    className={`mt-1 block w-full h-8 py-4 px-2 rounded-md border ${errors.zip ? 'border-red-500' : 'border-gray-300'} shadow-sm focus:border-blue-900 focus:ring focus:ring-blue-900 focus:ring-opacity-50`}
                                    required
                                    placeholder="Par ex: 75001"
                                />
                                {errors.zip && (
                                    <div className="p-2 bg-red-100 text-red-700 rounded-md mt-1 text-sm">
                                        {errors.zip}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Animation for the progress bar
    const progressBarAnimation = (num) => {
        if (step > num) return 'w-16 md:w-48 h-1 mx-2 bg-blue-900 transition-all duration-500';
        if (step === num) return 'w-16 md:w-48 h-1 mx-2 bg-gray-200 transition-all duration-500';
        return 'w-16 md:w-48 h-1 mx-2 bg-gray-200';
    };

    // Animation for the step circles
    const stepCircleAnimation = (num) => {
        const baseClasses = 'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500';
        if (step >= num) return `${baseClasses} bg-blue-900 text-white`;
        return `${baseClasses} bg-gray-200 text-gray-700`;
    };

    return (
        <div className="container sm:mt-5 lg:mt-48">
            <div className="flex items-center justify-center w-full h-screen md:h-auto">
                <div className="overflow-hidden">
                    {!submitted ? (
                        <>
                            <div className="mb-8">
                                <div className="flex justify-between items-center">
                                    {[1, 2, 3].map((num) => (
                                        <div key={num} className="flex items-center">
                                            <div className={stepCircleAnimation(num)}>
                                                {num}
                                            </div>
                                            {num < 3 && (
                                                <div className={progressBarAnimation(num)} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative min-h-full overflow-hidden">
                                <form onSubmit={handleSubmit} className="w-full">
                                    {renderStep()}

                                    {errors.general && (
                                        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
                                            {errors.general}
                                        </div>
                                    )}

                                    <div className="mt-8 flex justify-between">
                                        {step > 1 && (
                                            <button
                                                type="button"
                                                onClick={prevStep}
                                                className="flex items-center px-4 py-2 text-md font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-300"
                                                disabled={animating || loading}
                                            >
                                                <ArrowLeft className="w-4 h-4 mr-2"/>
                                                Précédent
                                            </button>
                                        )}
                                        {step < 3 ? (
                                            <button
                                                type="button"
                                                onClick={nextStep}
                                                className="flex items-center px-4 py-2 text-md font-medium text-white bg-blue-900 rounded-md hover:bg-blue-800 ml-auto transition-colors duration-300"
                                                disabled={animating || loading}
                                            >
                                                Suivant
                                                <ArrowRight className="w-4 h-4 ml-2"/>
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="flex items-center px-4 py-2 text-md font-medium text-white bg-green-600 rounded-md hover:bg-green-500 ml-auto transition-colors duration-300"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                        Traitement...
                                                    </>
                                                ) : (
                                                    <>
                                                        Soumettre
                                                        <Check className="w-4 h-4 ml-2"/>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white rounded-lg shadow-lg p-8 text-center animate-fadeIn">
                            <div className="flex justify-center mb-6">
                                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                                    <Check className="text-green-600 w-12 h-12" />
                                </div>
                            </div>

                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-blue-900 mb-4">Merci beaucoup !</h2>
                                <div className="space-y-4">
                                    <p className="text-lg text-gray-700">Nous vous remercions d'avoir choisi nos services.</p>
                                    <p className="text-md text-gray-600">Un de nos experts vous contactera dans les prochaines 24 heures.</p>
                                </div>
                            </div>

                            <div className="py-4 flex justify-center">
                                <button
                                    onClick={resetForm}
                                    className="flex items-center gap-2 px-6 py-3 text-md font-medium text-white bg-blue-900 rounded-md hover:bg-blue-800 transition-all duration-300 transform hover:scale-105"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    Nouveau formulaire
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiStepForm;